import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  Divider,
} from "@mui/material";
import {
  Refresh,
  Edit,
  Delete,
  Visibility,
  Search,
  Clear,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import axiosInstance from "../../../services/axiosInstance";

/* ---------------- Styled Components ---------------- */

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  background: "linear-gradient(135deg, #4a90e2, #357abd)",
  color: theme.palette.common.white,
  position: "sticky",
  top: 0,
  zIndex: 2,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "background-color 0.2s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.selected,
  },
}));

/* ---------------- Component ---------------- */

const TeamMembersTable = ({ clientId, refreshTrigger }) => {
  const [teamMemberData, setTeamMemberData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (clientId) getMembersDetails();
  }, [clientId, refreshTrigger]);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredData(teamMemberData);
    } else {
      const q = searchQuery.toLowerCase();
      setFilteredData(
        teamMemberData.filter(
          (m) =>
            m.name.toLowerCase().includes(q) ||
            m.email.toLowerCase().includes(q)
        )
      );
    }
  }, [searchQuery, teamMemberData]);

  const getMembersDetails = async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getTeamMembersByClientId}/${clientId}`
      );

      if (res.data.success) {
        setTeamMemberData(res.data.data);
        if (!res.data.data.length) {
          setMessage({
            type: "info",
            text: "No team members found for this client.",
          });
        }
      }
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Failed to fetch team members.",
      });
      setTeamMemberData([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        mt: 4,
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Team Members
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {teamMemberData.length} total members
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          onClick={getMembersDetails}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} /> : <Refresh />}
        >
          Refresh
        </Button>
      </Box>

      <Divider />

      {/* Search */}
      <Box sx={{ px: 3, py: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name or email"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery("")}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Alerts */}
      {message.text && (
        <Box sx={{ px: 3 }}>
          <Alert
            severity={message.type}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        </Box>
      )}

      {/* Table */}
      {loading ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : filteredData.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography variant="h6">
            {searchQuery ? "No matching members" : "No team members yet"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {searchQuery
              ? "Try a different keyword"
              : "Add a team member to get started"}
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Email</StyledTableCell>
                  <StyledTableCell>Role</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Created</StyledTableCell>
                  <StyledTableCell>Last Login</StyledTableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((m) => (
                    <StyledTableRow key={m._id}>
                      <TableCell>{m.name}</TableCell>
                      <TableCell>{m.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={m.role.toUpperCase()}
                          size="small"
                          color={
                            m.role === "admin"
                              ? "error"
                              : m.role === "member"
                                ? "primary"
                                : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={m.isActive ? "Active" : "Inactive"}
                          size="small"
                          color={m.isActive ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(m.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {m.lastLogin
                          ? new Date(m.lastLogin).toLocaleDateString()
                          : "Never"}
                      </TableCell>
                    </StyledTableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />

          {/* Pagination */}
          <TablePagination
            component="div"
            rowsPerPageOptions={[5, 10, 25]}
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}
    </Paper>
  );
};

export default TeamMembersTable;
